import { Typography } from "antd"
import dayjs from "dayjs"

const { Text, Link } = Typography

const statusDeadlines = {
  pending: {
    ethoca: { min: 1, max: 5 },
    cdrn: { min: 1, max: 3 },
    rdr: { min: 7, max: 14 }
  },
  pending_unenroll: {
    ethoca: { min: 2, max: 5 },
    cdrn: { min: 1, max: 3 },
    rdr: { min: 4, max: 8 }
  },
  in_re_enrollment: {
    ethoca: { min: 2, max: 5 },
    cdrn: { min: 1, max: 3 },
    rdr: { min: 7, max: 14 }
  },
  in_progress: {
    ethoca: { min: 1, max: 5 },
    cdrn: { min: 1, max: 3 },
    rdr: { min: 7, max: 14 }
  }
}

const descriptorStatusTextMap = {
  active: () => "Chargeback alerts are enabled. You are now protected.",
  pending: ({ isBalanceToppedUp, enrollmentStartDate, service }) => {
    if (!isBalanceToppedUp) {
      return "Balance top-up needed to continue enrollment."
    }
    const date = enrollmentStartDate
    const { min, max } = statusDeadlines.pending[service]
    if (getIsDeadlinePassed(date, max)) {
      const serviceName = service === "ethoca" ? "Ethoca" : "Verifi"
      return `Enrollment has been delayed. Our team is aware and have escalated the issue directly to ${serviceName}. We are monitoring the situation and and will keep you updated.`
    }
    return (
      <Text type="secondary">
        <strong>Expected to activate {getDateString(date, min, max)}.</strong> No action required.
      </Text>
    )
  },
  pending_unenroll: ({ enrollmentStartDate, service }) => {
    const date = enrollmentStartDate
    const { min, max } = statusDeadlines.pending_unenroll[service]
    if (getIsDeadlinePassed(date, max)) {
      const serviceName = service === "ethoca" ? "Ethoca" : "Verifi"
      return `Unenrollment has been delayed. Our team is aware and have escalated the issue directly to ${serviceName}. We are monitoring the situation and and will keep you updated.`
    }
    return (
      <Text type="secondary">
        Unenrollment request is sent.{" "}
        <strong>Expected to be unenrolled {getDateString(date, min, max)}.</strong> You may still
        receive alerts until unenrollment completes.
      </Text>
    )
  },
  in_re_enrollment: ({ enrollmentStartDate, service }) => {
    const date = enrollmentStartDate
    const { min, max } = statusDeadlines.in_re_enrollment[service]
    if (getIsDeadlinePassed(date, max)) {
      const serviceName = service === "ethoca" ? "Ethoca" : "Verifi"
      return `Enrollment has been delayed. Our team is aware and have escalated the issue directly to ${serviceName}. We are monitoring the situation and and will keep you updated.`
    }
    return (
      <Text type="secondary">
        <strong>Expected to reactivate {getDateString(date, min, max)}.</strong> No action required.
      </Text>
    )
  },
  in_progress: ({ enrollmentStartDate, service, previousStatus, lastStatusChangeDate }) => {
    const isPreviousStatusDeadline = ["pending", "in_re_enrollment"].includes(previousStatus)
    const deadlinesSource = isPreviousStatusDeadline
      ? statusDeadlines[previousStatus]
      : statusDeadlines.in_progress
    const dateSource = isPreviousStatusDeadline ? enrollmentStartDate : lastStatusChangeDate
    const { min, max } = deadlinesSource[service]
    if (getIsDeadlinePassed(dateSource, max)) {
      const serviceName = service === "ethoca" ? "Ethoca" : "Verifi"
      return `Enrollment has been delayed. Our team is aware and have escalated the issue directly to ${serviceName}. We are monitoring the situation and and will keep you updated.`
    }
    return (
      <Text type="secondary">
        Enrollment request is sent.{" "}
        <strong>Expected to activate {getDateString(dateSource, min, max)}.</strong> No action
        required.
      </Text>
    )
  },
  enrolled_elsewhere: () =>
    "Enrollment failed. Descriptor is enrolled with another provider. Ask them to transfer the descriptor to Chargeback, or to release your descriptor.",
  generic_descriptor: () => (
    <Text type="secondary">
      Enrollment failed. Your descriptor is too generic. Enrolling it would lead to alerts that are
      not yours. Update it to a unique descriptor.{" "}
      <Link
        href="https://help.chargeback.io/en/articles/12896177-best-practices-for-billing-descriptors"
        target="_blank"
      >
        View best practices.
      </Link>
    </Text>
  ),
  banned_descriptor: () =>
    "Enrollment failed. This descriptor isn’t eligible for enrollment. Contact our support for more details.",
  invalid_descriptor: () =>
    "Enrollment failed. This descriptor can’t be enrolled as entered. Contact support for help.",
  unenrolled: ({ service }) =>
    `You’ll no longer receive alerts for this descriptor and it has been released from ${
      service === "ethoca" ? "Ethoca" : "Verifi"
    }.`,
  verification_needed: () =>
    "Verification required. Our support will reach out within 48 hours with more details. Please watch your email inbox.",
  invalid_arns: () => (
    <Text type="secondary">
      Enrollment failed. The provided ARNs might be older than 90 days. Use different ARNs or enroll
      with your{" "}
      <Link
        href="https://help.chargeback.io/en/articles/12782785-how-to-get-bins-caid-for-rdr-alerts"
        target="_blank"
      >
        BIN(s) and CAID(s).
      </Link>
    </Text>
  ),
  needs_assistance: () => (
    <Text type="secondary">
      Action needed. Provide your ARNs, or your{" "}
      <Link
        href="https://help.chargeback.io/en/articles/12782785-how-to-get-bins-caid-for-rdr-alerts"
        target="_blank"
      >
        BIN(s) and CAID(s)
      </Link>
      , to complete enrollment. Our support will reach out within 48 hours on your email for
      assistance.
    </Text>
  )
}

const getIsDeadlinePassed = (date, plusDays) => {
  if (!date) return false
  const deadline = dayjs(date).add(plusDays, "day")
  return dayjs().utc().isAfter(deadline)
}

const getDateString = (date, plusMin, plusMax) => {
  if (!date) return ""

  const baseDate = dayjs(date)
  const minDate = baseDate.add(plusMin, "day")
  const maxDate = baseDate.add(plusMax, "day")

  const now = dayjs().utc()
  if (maxDate.isBefore(now)) {
    return "within the next few days"
  }

  const minMonth = minDate.format("MMM")
  const maxMonth = maxDate.format("MMM")
  const year = maxDate.format("YYYY")
  const minDay = minDate.format("D")
  const maxDay = maxDate.format("D")

  if (minMonth === maxMonth) {
    return `between ${minMonth} ${minDay} and ${maxDay}, ${year}`
  }

  return `between ${minMonth} ${minDay} and ${maxMonth} ${maxDay}, ${year}`
}

const DescriptorStatusText = ({
  status,
  service,
  enrollmentStartDate,
  isBalanceToppedUp,
  lastStatusChangeDate,
  previousStatus
}) => {
  const getStatusText = descriptorStatusTextMap[status] || (() => status)
  return (
    <Text type="secondary">
      {getStatusText({
        service,
        enrollmentStartDate,
        isBalanceToppedUp,
        lastStatusChangeDate,
        previousStatus
      })}
    </Text>
  )
}

export default DescriptorStatusText
